import { Component, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { environment } from '../../../../environments/environment';
import { AdminSpawn, CardsAdminService } from '../../../services/admin/cards-admin.service';

@Component({
  selector: 'app-cards-spawns-tab',
  imports: [TranslocoModule, ReactiveFormsModule, DatePipe],
  templateUrl: './cards-spawns-tab.component.html',
  styleUrl: './cards-spawns-tab.component.scss'
})
export class CardsSpawnsTabComponent implements OnInit, OnDestroy {
  private map: mapboxgl.Map;
  private markers: mapboxgl.Marker[] = [];
  private previewMarker: mapboxgl.Marker | null = null;

  spawns = rxResource({ stream: () => this.cardsAdmin.getSpawns$(), defaultValue: [] });
  cards = rxResource({ stream: () => this.cardsAdmin.getCards$(), defaultValue: [] });
  clickedCoords = signal<{ lng: number; lat: number } | null>(null);
  selectedCardId = new FormControl<number | null>(null);
  expiresDays = new FormControl<number | null>(null);

  constructor(
    private cardsAdmin: CardsAdminService,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar
  ) {
    effect(() => this.drawSpawnMarkers(this.spawns.value()));
  }

  ngOnInit(): void {
    // @ts-ignore
    mapboxgl.config.ACCESS_TOKEN = environment.mapboxPublicToke;
    // @ts-ignore
    this.map = new mapboxgl.Map({
      container: 'cards-map-div',
      style: 'mapbox://styles/mapbox/outdoors-v12',
      zoom: 5,
      center: [30.52, 50.45]
    });
    this.map.addControl(new MapboxGeocoder({ accessToken: environment.mapboxPublicToke, mapboxgl: mapboxgl }));
    this.map.addControl(new mapboxgl.NavigationControl());
    // The spawns resource can resolve before the map finishes loading (or vice versa) - the constructor
    // effect already redraws markers whenever spawns changes, but this covers the case where the
    // resource resolved first, so we need one more draw once the map itself becomes ready.
    this.map.on('load', () => this.drawSpawnMarkers(this.spawns.value()));
    this.map.on('click', (ev) => {
      const coords = { lng: parseFloat(ev.lngLat.lng.toFixed(6)), lat: parseFloat(ev.lngLat.lat.toFixed(6)) };
      this.clickedCoords.set(coords);
      this.updatePreviewMarker(coords);
    });
  }

  ngOnDestroy(): void {
    this.markers.forEach((m) => m.remove());
    this.markers = [];
    this.previewMarker?.remove();
    this.previewMarker = null;
    this.map?.remove();
  }

  createSpawn(): void {
    const coords = this.clickedCoords();
    const cardId = this.selectedCardId.value;
    if (!coords || !cardId) {
      return;
    }
    this.cardsAdmin.createSpawn$(cardId, coords.lng, coords.lat, this.expiresDays.value ?? undefined).subscribe(() => {
      this.snackBar.open(this.transloco.translate('cardsAdminSpawned'), '', { duration: 3000 });
      this.clickedCoords.set(null);
      this.previewMarker?.remove();
      this.previewMarker = null;
      this.spawns.reload();
    });
  }

  deleteSpawn(id: number): void {
    this.cardsAdmin.deleteSpawn$(id).subscribe(() => this.spawns.reload());
  }

  private updatePreviewMarker(coords: { lng: number; lat: number }): void {
    this.previewMarker?.remove();
    const el = document.createElement('div');
    el.style.width = '16px';
    el.style.height = '16px';
    el.style.borderRadius = '50%';
    el.style.background = 'var(--atlas-color-primary)';
    el.style.border = '2px solid white';
    this.previewMarker = new mapboxgl.Marker(el).setLngLat([coords.lng, coords.lat]).addTo(this.map);
  }

  private createSpawnMarkerElement(s: AdminSpawn): HTMLElement {
    const color = s.isAdminSpawn ? 'var(--atlas-color-primary)' : 'var(--atlas-secondary-color-step-600)';

    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.style.alignItems = 'center';

    const img = document.createElement('img');
    img.src = s.thumbUrl;
    img.alt = 'card';
    img.style.width = '28px';
    img.style.borderRadius = '4px';
    img.style.border = `2px solid ${color}`;

    const pin = document.createElement('div');
    pin.style.width = '6px';
    pin.style.height = '6px';
    pin.style.marginTop = '2px';
    pin.style.borderRadius = '50%';
    pin.style.background = color;

    wrapper.append(img, pin);
    return wrapper;
  }

  private drawSpawnMarkers(spawns: AdminSpawn[]): void {
    if (!this.map) {
      return;
    }
    this.markers.forEach((m) => m.remove());
    this.markers = spawns.map((s) =>
      new mapboxgl.Marker(this.createSpawnMarkerElement(s), { anchor: 'bottom' })
        .setLngLat([s.lng, s.lat])
        .addTo(this.map)
    );
  }
}
